import React, { useState, useEffect } from 'react';
import { FileText, FileSpreadsheet, FileImage, File, FileVideo, FileAudio, FileCode, FileArchive, Presentation } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useDocumentCache } from '@/hooks/useDocumentCache';

interface FileThumbnailProps {
  fileUrl?: string;
  fileType: string;
  fileName?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showLabel?: boolean;
}

const sizeClasses = {
  sm: 'w-10 h-12',
  md: 'w-16 h-20',
  lg: 'w-24 h-32',
};

const iconSizes = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
};

const getFileTypeInfo = (fileType: string, fileName?: string) => {
  const lowerType = fileType.toLowerCase();
  const lowerName = fileName?.toLowerCase() || '';

  // Links
  if (lowerType === 'link') {
    return { icon: FileText, color: 'bg-chart-2', label: 'Link' };
  }

  // Images
  if (lowerType.includes('image') || lowerName.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/)) {
    return { icon: FileImage, color: 'bg-chart-2', label: 'Bild' };
  }

  // PDFs
  if (lowerType.includes('pdf') || lowerName.endsWith('.pdf')) {
    return { icon: FileText, color: 'bg-destructive', label: 'PDF' };
  }

  // Excel
  if (lowerType.includes('excel') || lowerType.includes('spreadsheet') || lowerName.match(/\.(xlsx?|csv)$/)) {
    return { icon: FileSpreadsheet, color: 'bg-chart-5', label: 'Excel' };
  }

  // Word
  if (lowerType.includes('word') || lowerType.includes('document') || lowerName.match(/\.docx?$/)) {
    return { icon: FileText, color: 'bg-chart-2', label: 'Word' };
  }

  // PowerPoint
  if (lowerType.includes('presentation') || lowerName.match(/\.pptx?$/)) {
    return { icon: Presentation, color: 'bg-primary', label: 'PPT' };
  }

  // Video
  if (lowerType.includes('video') || lowerName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/)) {
    return { icon: FileVideo, color: 'bg-chart-4', label: 'Video' };
  }

  // Audio
  if (lowerType.includes('audio') || lowerName.match(/\.(mp3|wav|ogg|m4a|flac)$/)) {
    return { icon: FileAudio, color: 'bg-chart-4', label: 'Audio' };
  }

  // Code
  if (lowerName.match(/\.(js|jsx|ts|tsx|py|java|cpp|c|h|css|html|json|xml|yml|yaml)$/)) {
    return { icon: FileCode, color: 'bg-chart-1', label: 'Code' };
  }

  // Archives
  if (lowerName.match(/\.(zip|rar|7z|tar|gz)$/)) {
    return { icon: FileArchive, color: 'bg-muted-foreground', label: 'Archiv' };
  }

  // Default
  return { icon: File, color: 'bg-muted-foreground', label: 'Datei' };
};

export const FileThumbnail: React.FC<FileThumbnailProps> = ({
  fileUrl,
  fileType,
  fileName,
  size = 'md',
  className,
  showLabel = false,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { targetRef, hasIntersected } = useIntersectionObserver({ 
    threshold: 0.1,
    rootMargin: '100px' 
  });
  const { getCachedThumbnail, setCachedThumbnail } = useDocumentCache();

  const fileInfo = getFileTypeInfo(fileType, fileName);
  const Icon = fileInfo.icon;
  const isImage = fileInfo.label === 'Bild';

  useEffect(() => {
    if (!hasIntersected || !fileUrl || !isImage || imageError) return;

    // Check cache first
    const cachedUrl = getCachedThumbnail(fileUrl);
    if (cachedUrl) {
      setImageSrc(cachedUrl);
      return;
    }

    // Load image lazily
    setIsLoading(true);
    const img = new Image();
    img.onload = () => {
      setImageSrc(fileUrl);
      setCachedThumbnail(fileUrl, fileUrl);
      setIsLoading(false);
    };
    img.onerror = () => {
      setImageError(true);
      setIsLoading(false);
    };
    img.src = fileUrl;
  }, [hasIntersected, fileUrl, isImage, imageError, getCachedThumbnail, setCachedThumbnail]);

  // Show image thumbnail for image files
  if (isImage && fileUrl && !imageError) {
    return (
      <div 
        ref={targetRef}
        className={cn(
          'relative overflow-hidden rounded-lg shadow-sm',
          sizeClasses[size],
          className
        )}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Skeleton className="w-full h-full" />
          </div>
        )}
        {imageSrc && (
          <img
            src={imageSrc}
            alt={fileName || 'Thumbnail'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
        {showLabel && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1">
            <span className="text-white text-xs truncate block">{fileInfo.label}</span>
          </div>
        )}
      </div>
    );
  }

  // Show icon for non-image files
  return (
    <div
      ref={targetRef}
      className={cn(
        'relative flex flex-col items-center justify-center rounded-lg shadow-sm',
        fileInfo.color,
        sizeClasses[size],
        className
      )}
    >
      <Icon className={cn('text-white', iconSizes[size])} />
      {showLabel && (
        <span className="text-white text-xs mt-1 font-medium">{fileInfo.label}</span>
      )}
    </div>
  );
};
